import moment from 'moment';
import * as R from 'ramda';
import changeLogTypes from '../change-log-types.json';

export default function ChangeLogDialogController(
  $rootScope,
  $scope,
  alerts,
  loaderService,
  ssDialogService,
  pmDataService,
  pmSharedModelService,
  utilityService
) {
  const vm = this;

  vm.showFilters = true;

  vm.handleCloseDialogClick = _handleCloseDialogClick;
  vm.handleClearFiltersClick = _handleClearFiltersClick;
  vm.handleToggleFiltersClick = _handleToggleFiltersClick;
  vm.handleCheckAllClick = _handleCheckAllClick;
  vm.handleUncheckAllClick = _handleUncheckAllClick;

  _activate();

  // //////// HANDLERS //////////

  function _handleCloseDialogClick() {
    _closeDialog();
  }

  function _handleToggleFiltersClick() {
    vm.showFilters = !vm.showFilters;
  }

  function _handleClearFiltersClick() {
    _clearFilters();
    _filterItems();
  }

  function _handleCheckAllClick() {
    _toggleAllFilterCheckboxes(true);
    _filterItems();
  }

  function _handleUncheckAllClick() {
    _toggleAllFilterCheckboxes(false);
    _filterItems();
  }

  // //////// METHODS //////////

  function _activate() {
    _initWatchers();
    _initEventListeners();
    _initStartingFilters();
    _clearFilters();
  }

  function _closeDialog() {
    return ssDialogService.hide(ssDialogService.dialogs.pm.changeLogDialog);
  }

  function _initWatchers() {
    $scope.$watch(() => vm.filters, () => {
      _filterItems();
    }, true);
  }

  function _initEventListeners() {
    $scope.$on('refreshPmChangeLog', (event, opt) => {
      loaderService.showViewLoader = true;
      if (opt.policyDocumentId) {
        pmDataService.getChangeLog(opt.policyDocumentId).then(
          (getChangeLogResponse) => {
            loaderService.showViewLoader = false;
            vm.changeLogItems = getChangeLogResponse.data.result;
            _setIsRevertable();
            _initStartingFilters();
            _clearFilters();
            _filterItems();
            _initChangeLogItems();
          }
        );
      } else {
        pmDataService.getChangeLogByCustomer().then(
          (getChangeLogByCustomerResponse) => {
            loaderService.showViewLoader = false;
            vm.changeLogItems = getChangeLogByCustomerResponse.data;
            _setIsRevertable();
            _initStartingFilters();
            _clearFilters();
            _filterItems();
            _initChangeLogItems();
          }
        );
      }
    });
  }

  function _setIsRevertable() {
    vm.changeLogItems.forEach((item) => {
      if ((item.changeLogTypeName === 'Section Created')
        || (item.changeLogTypeName === 'Document Approved Date')) {
        item.isRevertable = false;
      } else {
        item.isRevertable = true;
      }
    });
  }
  function _toggleAllFilterCheckboxes(setChecked) {
    vm.filters = {
      ...vm.filters,
      includeSectionTitles: setChecked,
      includeSectionBodies: setChecked,
      includeSectionDetails: setChecked,
      includeVariables: setChecked,
      includeEndnotes: setChecked,
      includeBoardApproved: setChecked,
      includeRecommended: setChecked,
      includeInstitutionChanges: setChecked,
      includeAcceptedRecommendations: setChecked,
      includeRejectedRecommendations: setChecked,
      includeMaterialChanges: setChecked,
      includeImmaterialChanges: setChecked,
      includeDocumentSectionCodeTypes: setChecked,
    };
  }

  function _initStartingFilters() {
    vm.clearedFilters = {
      keywordText: '',
      includeSectionTitles: true,
      includeSectionBodies: true,
      includeSectionDetails: true,
      includeVariables: true,
      includeEndnotes: true,
      includeBoardApproved: true,
      includeRecommended: true,
      includeInstitutionChanges: true,
      includeAcceptedRecommendations: true,
      includeRejectedRecommendations: true,
      includeMaterialChanges: true,
      includeImmaterialChanges: true,
      fromDate: moment().add(-1, 'y'),
      toDate: moment().endOf('day'),
      includeDocumentNameChanges: true,
      includeDocumentSectionCodeTypes: false,
    };
  }

  function _clearFilters() {
    vm.filters = R.clone(vm.clearedFilters);
  }

  function _filterItems() {
    vm.filteredChangeLogItems = R.filter(
      (changeLogItem) => {
        const inDateRange = moment(changeLogItem.createdDate).isBetween(vm.filters.fromDate, vm.filters.toDate);
        const keywordTextMatches = _checkKeywordTextMatches(changeLogItem, vm.filters.keywordText);
        const typeIsIncluded = _checkTypeIsIncluded(changeLogItem, vm.filters, changeLogTypes);

        if (changeLogItem.changeLogTypeId === 18) {
          changeLogItem.include = 1;
        }

        const changeSourceIsIncluded = _checkChangeSourceIsIncluded(changeLogItem, vm.filters);
        const materialTypeIsIncluded = _checkMaterialTypeIsIncluded(changeLogItem, vm.filters);

        return keywordTextMatches && inDateRange && typeIsIncluded && changeSourceIsIncluded && materialTypeIsIncluded;
      },
      vm.changeLogItems
    );
  }

  function _checkKeywordTextMatches(changeLogItem, keywordText) {
    if (!keywordText) {
      changeLogItem.isExpanded = false;
      return true;
    }

    if (
      (changeLogItem.change && _checkTextSnippetMatches(changeLogItem.change, keywordText))
      || _checkTextSnippetMatches(changeLogItem.createdUserName, keywordText)
    ) {
      return true;
    }

    if ((
      changeLogItem.tokenizedUnformattedPreviousValue
      && _checkTextSnippetMatches(changeLogItem.tokenizedUnformattedPreviousValue, keywordText)
    ) || (
      changeLogItem.tokenizedUnformattedCurrentValue
      && _checkTextSnippetMatches(changeLogItem.tokenizedUnformattedCurrentValue, keywordText)
    )) {
      changeLogItem.isExpanded = true;
      return true;
    }
    changeLogItem.isExpanded = false;

    return false;
  }

  function _checkTextSnippetMatches(textToCheck, textToMatch) {
    return textToCheck.toLowerCase().indexOf(textToMatch.toLowerCase()) > -1;
  }

  function _checkTypeIsIncluded(changeLogItem, filters, changeLogTypeConstants) {
    return (
      (
        changeLogItem.changeLogTypeId === changeLogTypeConstants.SECTION_TITLE.changeLogTypeId
        || changeLogItem.changeLogTypeId === changeLogTypeConstants.MASTER_SECTION_TITLE.changeLogTypeId
      )
      && filters.includeSectionTitles
    ) || (
      (
        changeLogItem.changeLogTypeId === changeLogTypeConstants.SECTION_BODY.changeLogTypeId
        || changeLogItem.changeLogTypeId === changeLogTypeConstants.MASTER_SECTION_BODY.changeLogTypeId
      )
      && filters.includeSectionBodies
    ) || (
      (
        changeLogItem.changeLogTypeId === changeLogTypeConstants.SECTION_DETAILS.changeLogTypeId
      )
      && filters.includeSectionDetails
    ) || (
      changeLogItem.changeLogTypeId === changeLogTypeConstants.VARIABLE_VALUE.changeLogTypeId
      && filters.includeVariables
    ) || (
      (
        changeLogItem.changeLogTypeId === changeLogTypeConstants.ENDNOTE_TEXT.changeLogTypeId
        || changeLogItem.changeLogTypeId === changeLogTypeConstants.MASTER_ENDNOTE_TEXT.changeLogTypeId
        || changeLogItem.changeLogTypeId === changeLogTypeConstants.MASTER_ENDNOTE_CREATED.changeLogTypeId
        || changeLogItem.changeLogTypeId === changeLogTypeConstants.ENDNOTE_CREATED.changeLogTypeId
      )
      && filters.includeEndnotes
    ) || (
      (changeLogItem.changeLogTypeId === changeLogTypeConstants.DOCUMENT_APPROVED.changeLogTypeId)
      && filters.includeBoardApproved
    ) || (
      (changeLogItem.changeLogTypeId === changeLogTypeConstants.Section_Created.changeLogTypeId)
      && (filters.includeSectionBodies || filters.includeSectionTitles)
    ) || (
      (changeLogItem.changeLogTypeId === changeLogTypeConstants.DOCUMENT_NAME.changeLogTypeId)
      && filters.includeDocumentNameChanges
    ) || (
      // eslint-disable-next-line max-len
      (changeLogItem.changeLogTypeId === changeLogTypeConstants.Document_Section_Code.changeLogTypeId)
      // eslint-disable-next-line max-len
      && (filters.includeDocumentSectionCodeTypes)
    ) || (
      // eslint-disable-next-line max-len
      (changeLogItem.changeLogTypeId === changeLogTypeConstants.Section_Deleted.changeLogTypeId)
      && (filters.includeSectionBodies || filters.includeSectionTitles)
    );
  }

  function _checkChangeSourceIsIncluded(changeLogItem, filters) {
    return (
      (changeLogItem.recommendedChange && filters.includeRecommended)
      || (!changeLogItem.recommendedChange && filters.includeInstitutionChanges)
    );
  }

  function _checkMaterialTypeIsIncluded(changeLogItem, filters) {
    return (
      (changeLogItem.isMaterialChange && filters.includeMaterialChanges)
      || (!changeLogItem.isMaterialChange && filters.includeImmaterialChanges)
    );
  }

  function _initChangeLogItems() {
    R.forEach((item) => {
      _initData(item);
    }, vm.filteredChangeLogItems);
  }

  function _initData(changeLogItem) {
    changeLogItem.isExpanded = false;
    changeLogItem.createdDate = moment(changeLogItem.createdDate).format('MM-DD-YYYY');

    if (changeLogItem.changeLogTypeId === changeLogTypes.SECTION_BODY.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.MASTER_SECTION_BODY.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.SECTION_DETAILS.changeLogTypeId
      // || vm.changeLogItem.changeLogTypeId === changeLogTypes.ENDNOTE_TEXT.changeLogTypeId
    ) {
      changeLogItem.expandedPreviousValue = pmSharedModelService.replaceTokens(
        utilityService.stripHtml(changeLogItem.valuePrevious)
      );
      changeLogItem.expandedCurrentValue = pmSharedModelService.replaceTokens(
        utilityService.stripHtml(changeLogItem.valueCurrent)
      );
      const tooltipTruncateLength = 1000;
      const previousValueLength = changeLogItem.expandedPreviousValue.length;
      changeLogItem.previousValueTooltip = previousValueLength > tooltipTruncateLength
        ? `${changeLogItem.expandedPreviousValue.substr(0, tooltipTruncateLength - 1)}&hellip;`
        : changeLogItem.expandedPreviousValue;
      const currentValueLength = changeLogItem.expandedPreviousValue.length;
      changeLogItem.currentValueTooltip = currentValueLength > tooltipTruncateLength
        ? `${changeLogItem.expandedCurrentValue.substr(0, tooltipTruncateLength - 1)}&hellip;`
        : changeLogItem.expandedCurrentValue;
    } else {
      changeLogItem.expandedPreviousValue = changeLogItem.valuePrevious;
      changeLogItem.previousValueTooltip = changeLogItem.valuePrevious;
      changeLogItem.expandedCurrentValue = changeLogItem.valueCurrent;
      changeLogItem.currentValueTooltip = changeLogItem.valueCurrent;
    }

    // vm.changeLogItem.isExpandable = (vm.changeLogItem.changeLogTypeId === changeLogTypes.SECTION_BODY.changeLogTypeId
    //   || vm.changeLogItem.changeLogTypeId === changeLogTypes.MASTER_SECTION_BODY.changeLogTypeId
    //   || vm.changeLogItem.changeLogTypeId === changeLogTypes.SECTION_DETAILS.changeLogTypeId);

    changeLogItem.isRecommendation = (
      changeLogItem.changeLogTypeId === changeLogTypes.MASTER_SECTION_TITLE.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.MASTER_SECTION_BODY.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.MASTER_ENDNOTE_TEXT.changeLogTypeId
    );

    if (changeLogItem.changeLogTypeId === changeLogTypes.VARIABLE_VALUE.changeLogTypeId) {
      changeLogItem.listTitleCssOverride = 'rte-token pm-variable mt4 cd';
    }
    if (
      changeLogItem.changeLogTypeId === changeLogTypes.ENDNOTE_TEXT.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.MASTER_ENDNOTE_TEXT.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.MASTER_ENDNOTE_CREATED.changeLogTypeId
      || changeLogItem.changeLogTypeId === changeLogTypes.ENDNOTE_CREATED.changeLogTypeId
    ) {
      changeLogItem.listTitleCssOverride = 'rte-token pm-endnote mt4 cd';
    }

    // const changedBy = vm.changeLogItem.isRecommendation ? 'The system' : vm.changeLogItem.createdUserName;
    // const recommendedText = vm.isAcceptedRecommendedChange ? ' per recommendation' : '';
    //
    // vm.changeLogItem.changeDetailsTooltip = `${vm.changeLogItem.changeLogTypeName} was changed by ${changedBy} on
    // ${vm.changeLogItem.createdDate}${recommendedText}.`;

    if ((changeLogItem.changeLogTypeName === 'Section Created')
      || (changeLogItem.changeLogTypeName === 'Document Approved Date')) {
      changeLogItem.isRevertable = false;
    } else {
      changeLogItem.isRevertable = true;
    }
  }
}

ChangeLogDialogController.$inject = [
  '$rootScope',
  '$scope',
  'alerts',
  'loaderService',
  'ssDialogService',
  'pmDataService',
  'pmSharedModelService',
  'utilityService'
];
